import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  TextField,
} from '@mui/material';
import { Close, Business, Person, LocationOn, Work, CalendarToday, AttachMoney, CheckCircle, Cancel } from '@mui/icons-material';
import { apiService } from '../services/api';
import { displayName } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface RequisitionDetailModalProps {
  open: boolean;
  onClose: () => void;
  requisitionId: string | null;
  onUpdated: () => void;
}

const statusColor: Record<string, any> = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  open: 'success',
  filled: 'default',
  cancelled: 'error',
};

const RequisitionDetailModal: React.FC<RequisitionDetailModalProps> = ({
  open,
  onClose,
  requisitionId,
  onUpdated,
}) => {
  const { user } = useAuth();
  const [requisition, setRequisition] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState<{ open: boolean; action: 'approve' | 'reject' }>({ open: false, action: 'approve' });
  const [approvalComments, setApprovalComments] = useState('');

  useEffect(() => {
    if (open && requisitionId) {
      loadRequisition();
    }
  }, [open, requisitionId]);

  const loadRequisition = async () => {
    if (!requisitionId) return;
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getRequisition(requisitionId);
      setRequisition(data);
    } catch (err: any) {
      setError('Failed to load requisition details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!requisitionId) return;
    try {
      setActionLoading(true);
      await apiService.submitRequisitionForApproval(requisitionId);
      await loadRequisition();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to submit for approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprovalAction = async () => {
    if (!requisitionId) return;
    try {
      setActionLoading(true);
      if (approvalDialog.action === 'approve') {
        await apiService.approveRequisition(requisitionId, approvalComments || undefined);
      } else {
        await apiService.rejectRequisition(requisitionId, approvalComments || undefined);
      }
      setApprovalDialog({ open: false, action: 'approve' });
      setApprovalComments('');
      await loadRequisition();
      onUpdated();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to process approval');
    } finally {
      setActionLoading(false);
    }
  };

  // Derive whether the current logged-in user has a pending approval step
  // AND all prior steps (lower stepOrder) are already approved
  const myPendingApproval = (() => {
    if (!requisition?.approvals) return null;
    const sorted = [...requisition.approvals].sort((a: any, b: any) => a.stepOrder - b.stepOrder);
    const mine = sorted.find(
      (a: any) => a.approverId === user?.employee?.id && a.status === 'pending'
    );
    if (!mine) return null;
    const blockedByPrior = sorted.some(
      (a: any) => a.stepOrder < mine.stepOrder && a.status !== 'approved'
    );
    return blockedByPrior ? null : mine;
  })();

  const formatDate = (date: string) =>
    date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const formatCurrency = (amount: number, currency: string) =>
    amount ? `${currency || 'USD'} ${amount.toLocaleString()}` : null;

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {requisition ? requisition.title : 'Job Requisition'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {requisition && (
              <Chip
                label={requisition.status.replace(/_/g, ' ').toUpperCase()}
                color={statusColor[requisition.status] || 'default'}
                size="small"
              />
            )}
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && requisition && (
          <Box>
            {/* Key Details */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Business fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Entity</Typography>
                </Box>
                <Typography variant="body2">{requisition.entity?.name || '—'}</Typography>
              </Grid>
              <Grid size={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Work fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                </Box>
                <Typography variant="body2">{requisition.department?.name || '—'}</Typography>
              </Grid>
              <Grid size={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Hiring Manager</Typography>
                </Box>
                <Typography variant="body2">
                  {requisition.hiringManager ? displayName(requisition.hiringManager) : '—'}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                </Box>
                <Typography variant="body2">{requisition.location || '—'}</Typography>
              </Grid>
              <Grid size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Work fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Employment Type</Typography>
                </Box>
                <Typography variant="body2">
                  {requisition.employmentType?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Target Start</Typography>
                </Box>
                <Typography variant="body2">{formatDate(requisition.targetStartDate)}</Typography>
              </Grid>
              <Grid size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachMoney fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Budget</Typography>
                </Box>
                <Typography variant="body2">
                  {requisition.budgetMin || requisition.budgetMax
                    ? `${formatCurrency(requisition.budgetMin, requisition.currency) || ''} – ${formatCurrency(requisition.budgetMax, requisition.currency) || ''}`
                    : '—'}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Job Description */}
            <Typography variant="subtitle2" gutterBottom>Job Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2, color: 'text.secondary' }}>
              {requisition.jobDescription || '—'}
            </Typography>

            {requisition.requirements && (
              <>
                <Typography variant="subtitle2" gutterBottom>Requirements</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2, color: 'text.secondary' }}>
                  {requisition.requirements}
                </Typography>
              </>
            )}

            {/* Approval Workflow */}
            {requisition.approvals && requisition.approvals.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Approval Workflow</Typography>
                {(() => {
                  const sortedApprovals = [...requisition.approvals].sort((a: any, b: any) => a.stepOrder - b.stepOrder);
                  const activeStep = sortedApprovals.findIndex((a: any) => a.status !== 'approved');
                  return (
                    <Stepper alternativeLabel activeStep={activeStep === -1 ? sortedApprovals.length : activeStep} sx={{ mb: 2 }}>
                      {sortedApprovals.map((approval: any, idx: number) => (
                        <Step key={idx} completed={approval.status === 'approved'}>
                          <StepLabel
                            error={approval.status === 'rejected'}
                            optional={
                              <Typography variant="caption">
                                {approval.approver ? displayName(approval.approver) : '—'}
                                {approval.status === 'pending' && idx === activeStep && (
                                  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 600 }}>
                                    Awaiting
                                  </Typography>
                                )}
                                {approval.status === 'pending' && idx !== activeStep && (
                                  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                                    Not yet
                                  </Typography>
                                )}
                                {approval.decidedAt && (
                                  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {new Date(approval.decidedAt).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Typography>
                            }
                          >
                            {approval.approverRole?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  );
                })()}
              </>
            )}

            {/* Applications */}
            {requisition.applications && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Applications ({requisition.applications.length})
                </Typography>
                {requisition.applications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No applications yet.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Candidate</TableCell>
                          <TableCell>Stage</TableCell>
                          <TableCell>Applied</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requisition.applications.map((app: any) => (
                          <TableRow key={app.id}>
                            <TableCell>
                              {displayName(app.candidate)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={app.stage?.replace(/_/g, ' ').toUpperCase()}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{formatDate(app.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {requisition?.status === 'draft' && (
          <Button
            variant="contained"
            onClick={handleSubmitForApproval}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Submit for Approval'}
          </Button>
        )}
        {myPendingApproval && (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading}
              onClick={() => { setApprovalComments(''); setApprovalDialog({ open: true, action: 'reject' }); }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              disabled={actionLoading}
              onClick={() => { setApprovalComments(''); setApprovalDialog({ open: true, action: 'approve' }); }}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>

      {/* Approve / Reject confirmation dialog */}
      <Dialog open={approvalDialog.open} onClose={() => setApprovalDialog({ ...approvalDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalDialog.action === 'approve' ? '✅ Approve Requisition' : '❌ Reject Requisition'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom sx={{ mb: 2 }}>
            {requisition?.title} — {requisition?.department?.name}
          </Typography>
          <TextField
            fullWidth multiline rows={3}
            label={approvalDialog.action === 'approve' ? 'Comments (optional)' : 'Reason for rejection'}
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.target.value)}
            required={approvalDialog.action === 'reject'}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ ...approvalDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            color={approvalDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleApprovalAction}
            disabled={actionLoading || (approvalDialog.action === 'reject' && !approvalComments.trim())}
          >
            {actionLoading ? <CircularProgress size={20} /> : approvalDialog.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RequisitionDetailModal;
